import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Modal from "@intility/bifrost-react/Modal";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import { useState } from "react";
import { deleteTuples, readTuples, writeTuples } from "~/api/client";
import type { GrantProviderReadersResult } from "~/api/clients";
import { useGrantProviderReaders } from "~/api/clients";
import type { Tuple, TupleKey } from "~/api/fga-types";
import { useCapabilities, useIsProviderAdmin } from "~/auth/capabilities";
import { PageLayout } from "~/components/layout/PageLayout";
import { relationBadgeState } from "~/lib/relationBadges";
import BlueprintTupleWizard from "./BlueprintTupleWizard";

type CreateMode = "none" | "paste" | "wizard";

const EXAMPLE_JSON = `[
  { "user": "user:alice", "relation": "viewer", "object": "document:readme" },
  { "user": "user:bob", "relation": "editor", "object": "document:readme" }
]`;

export default function TupleBrowser() {
  const { can } = useCapabilities();
  const canWrite = can("authorization.write");
  const { isProviderAdmin } = useIsProviderAdmin();
  const [tuples, setTuples] = useState<Tuple[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [filterUser, setFilterUser] = useState("");
  const [filterRelation, setFilterRelation] = useState("");
  const [filterObject, setFilterObject] = useState("");

  const [createMode, setCreateMode] = useState<CreateMode>("none");
  const [tuplesJson, setTuplesJson] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantResult, setGrantResult] =
    useState<GrantProviderReadersResult | null>(null);
  const grantMutation = useGrantProviderReaders();

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    setLoaded(false);

    const all: Tuple[] = [];
    let token: string | undefined;

    try {
      do {
        const res = await readTuples({
          continuationToken: token,
          pageSize: 50,
          allowUnfiltered: true,
        });
        all.push(...res.tuples);
        token = res.continuationToken || undefined;
      } while (token);

      setTuples(all);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredTuples = tuples.filter((t) => {
    const u = filterUser.toLowerCase();
    const r = filterRelation.toLowerCase();
    const o = filterObject.toLowerCase();
    return (
      (!u || t.key.user.toLowerCase().includes(u)) &&
      (!r || t.key.relation.toLowerCase().includes(r)) &&
      (!o || t.key.object.toLowerCase().includes(o))
    );
  });

  const handleUpload = () => {
    if (!tuplesJson.trim()) return;
    setUploading(true);
    setUploadError(null);
    setSuccessMessage(null);

    let parsed: TupleKey[];
    try {
      parsed = JSON.parse(tuplesJson.trim());
      if (!Array.isArray(parsed)) {
        throw new Error("JSON must be an array of tuple objects");
      }
      for (const t of parsed) {
        if (!t.user || !t.relation || !t.object) {
          throw new Error(
            'Each tuple must have "user", "relation", and "object" fields',
          );
        }
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Invalid JSON");
      setUploading(false);
      return;
    }

    writeTuples({ writes: parsed })
      .then(() => {
        setSuccessMessage(`${parsed.length} tuple(s) written successfully`);
        setTuplesJson("");
        setCreateMode("none");
        if (loaded) loadAll();
      })
      .catch((err) => setUploadError(err.message))
      .finally(() => setUploading(false));
  };

  const handleDelete = (tuple: TupleKey) => {
    const label = `(${tuple.user}, ${tuple.relation}, ${tuple.object})`;
    if (!confirm(`Delete tuple ${label}?`)) return;

    const key = `${tuple.user}|${tuple.relation}|${tuple.object}`;
    setDeletingKey(key);
    setError(null);
    deleteTuples([tuple])
      .then(() => {
        setSuccessMessage(`Deleted ${label}`);
        if (loaded) loadAll();
      })
      .catch((err) => setError(err.message))
      .finally(() => setDeletingKey(null));
  };

  const handleWizardSuccess = (message: string) => {
    setSuccessMessage(message);
    setCreateMode("none");
    if (loaded) loadAll();
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Tuples" },
      ]}
      title="Relationship Tuples"
    >
      {successMessage && (
        <Message state="success" header="Success">
          {successMessage}
        </Message>
      )}

      <Section>
        <Section.Header>Actions</Section.Header>
        <Section.Content>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button small onClick={loadAll} disabled={loading}>
              {loading ? "Loading..." : "List all tuples"}
            </Button>
            {canWrite && (
              <>
                <Button
                  small
                  variant={createMode === "paste" ? "flat" : "filled"}
                  onClick={() =>
                    setCreateMode(createMode === "paste" ? "none" : "paste")
                  }
                >
                  {createMode === "paste" ? "Cancel" : "Import (paste JSON)"}
                </Button>
                <Button
                  small
                  variant={createMode === "wizard" ? "flat" : "filled"}
                  onClick={() =>
                    setCreateMode(createMode === "wizard" ? "none" : "wizard")
                  }
                >
                  {createMode === "wizard" ? "Cancel" : "Build from blueprint"}
                </Button>
              </>
            )}
            {isProviderAdmin && (
              <Button
                small
                onClick={() => {
                  setGrantResult(null);
                  setShowGrantModal(true);
                }}
              >
                Grant Intility readers
              </Button>
            )}
          </div>
        </Section.Content>
      </Section>

      {createMode === "paste" && (
        <Section>
          <Section.Header>Import Relationship Tuples</Section.Header>
          <Section.Content>
            <p className="bf-p">
              Paste a JSON array of tuples. Each tuple needs <code>user</code>,{" "}
              <code>relation</code>, and <code>object</code> fields.
            </p>
            <textarea
              value={tuplesJson}
              onChange={(e) => setTuplesJson(e.target.value)}
              placeholder={EXAMPLE_JSON}
              rows={12}
              spellCheck={false}
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.5,
                resize: "vertical",
                padding: 12,
                borderRadius: 4,
                border: "1px solid var(--bf-border-color)",
                backgroundColor: "var(--bf-base-bg)",
                color: "var(--bf-base-color)",
                tabSize: 2,
              }}
            />
          </Section.Content>
          {uploadError && (
            <Section.Content borderTop>
              <Message state="alert" header="Error">
                {uploadError}
              </Message>
            </Section.Content>
          )}
          <Section.Content borderTop>
            <Button
              onClick={handleUpload}
              disabled={uploading || !tuplesJson.trim()}
            >
              {uploading ? "Writing..." : "Write Tuples"}
            </Button>
          </Section.Content>
        </Section>
      )}

      {createMode === "wizard" && (
        <BlueprintTupleWizard
          onSuccess={handleWizardSuccess}
          onCancel={() => setCreateMode("none")}
        />
      )}

      {error && (
        <Message state="alert" header="Error">
          {error}
        </Message>
      )}

      {loaded && (
        <Section>
          <Section.Header>
            Tuples{" "}
            <Badge state="neutral">
              {filteredTuples.length}
              {filteredTuples.length !== tuples.length
                ? ` / ${tuples.length}`
                : ""}
            </Badge>
          </Section.Header>
          <Section.Content>
            <Grid cols={1} small={3} gap={12}>
              <Input
                label="Filter user"
                placeholder="user:alice"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                icon={faSearch}
                clearable
                small
              />
              <Input
                label="Filter relation"
                placeholder="viewer"
                value={filterRelation}
                onChange={(e) => setFilterRelation(e.target.value)}
                clearable
                small
              />
              <Input
                label="Filter object"
                placeholder="platform:acme/IDM"
                value={filterObject}
                onChange={(e) => setFilterObject(e.target.value)}
                clearable
                small
              />
            </Grid>
          </Section.Content>
          <Table noBorder>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Relation</Table.HeaderCell>
                <Table.HeaderCell>Object</Table.HeaderCell>
                <Table.HeaderCell>Timestamp</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredTuples.map((tuple) => {
                const key = `${tuple.key.user}|${tuple.key.relation}|${tuple.key.object}`;
                return (
                  <Table.Row key={key}>
                    <Table.Cell>
                      <Badge state="chill" pill>
                        {tuple.key.user}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        state={relationBadgeState(tuple.key.relation, "access")}
                      >
                        {tuple.key.relation}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge state="neutral" pill>
                        {tuple.key.object}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(tuple.timestamp).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell>
                      {canWrite && (
                        <Button
                          small
                          variant="flat"
                          state="alert"
                          onClick={() => handleDelete(tuple.key)}
                          disabled={deletingKey === key}
                          title="Delete tuple"
                        >
                          {deletingKey === key ? "…" : "×"}
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
              {filteredTuples.length === 0 && (
                <Table.Row>
                  <Table.Cell>No tuples match the current filter</Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Section>
      )}
      {showGrantModal && (
        <Modal
          isOpen
          header="Grant Intility readers"
          onRequestClose={() => setShowGrantModal(false)}
        >
          {grantResult ? (
            <>
              <Message
                state={grantResult.failed > 0 ? "alert" : "success"}
                header="Result"
              >
                <p>
                  Granted: {grantResult.granted} |{" "}
                  <span style={{ color: "var(--bfc-status-warning)" }}>
                    Unchanged: {grantResult.unchanged}
                  </span>{" "}
                  | Skipped: {grantResult.skipped} | Failed:{" "}
                  {grantResult.failed} | Total: {grantResult.total}
                </p>
                {grantResult.unchanged > 0 && (
                  <p>
                    Unchanged means the user already had this role. No change
                    was made for them.
                  </p>
                )}
                {grantResult.failed > 0 && (
                  <p>
                    Failed means a rule refused that member. The other members
                    were granted. It is safe to run this again.
                  </p>
                )}
                {grantResult.errors.length > 0 && (
                  <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                    {grantResult.errors.slice(0, 10).map((e) => (
                      <li key={e} style={{ fontSize: 12 }}>
                        {e}
                      </li>
                    ))}
                    {grantResult.errors.length > 10 && (
                      <li style={{ fontSize: 12 }}>
                        …and {grantResult.errors.length - 10} more
                      </li>
                    )}
                  </ul>
                )}
              </Message>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Button onClick={() => setShowGrantModal(false)}>Close</Button>
              </div>
            </>
          ) : (
            <>
              <p className="bf-p">
                This will grant <strong>reader</strong> on{" "}
                <code>provider:intility</code> to all members of the Intility
                organization. Users who already have this role will be skipped.
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Button variant="flat" onClick={() => setShowGrantModal(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={grantMutation.isPending}
                  onClick={() => {
                    grantMutation.mutate(undefined, {
                      onSuccess: (result) => {
                        setGrantResult(result);
                        if (loaded) loadAll();
                      },
                      // The request itself failed, so the server's summary never
                      // arrived. The run is one request now (ADR-0047), so it was
                      // almost certainly never judged, but the activity log is the
                      // authority on what landed.
                      onError: (err) =>
                        setGrantResult({
                          total: 0,
                          granted: 0,
                          unchanged: 0,
                          skipped: 0,
                          failed: 1,
                          errors: [err.message],
                        }),
                    });
                  }}
                >
                  {grantMutation.isPending ? "Granting…" : "Grant readers"}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </PageLayout>
  );
}
